// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The Three.js scene host (M3-01).
//
// Owns the renderer, the scene, the camera and the canvas — and owns **none of the decisions**.
// Sizing is `renderSize.ts`, recovery is `contextLoss.ts`, tier budgets are `tiers.ts`, and the
// clock belongs to `SimulationDriver`. What is left here is the part that genuinely needs a
// GPU, which is also the part no headless test can reach, so there is as little of it as the
// job allows.
//
// **The one invariant.** `render(alpha)` draws an interpolation between two simulation states
// and never advances the simulation (PH-022, ARCH-012). The host has no `step()`, takes no
// `deltaTime`, and cannot advance anything even by accident: it is not given the game.
//
// The scene's contents are built by a `SceneBuilder` the caller supplies, and rebuilt from
// scratch after a context loss. That indirection is not decoration — every texture, buffer and
// program is invalid after a loss, so "rebuild the scene" has to be a thing the host can *do*,
// which means somebody other than the host has to know how.

import {
  ACESFilmicToneMapping,
  Color,
  PerspectiveCamera,
  Scene,
  SRGBColorSpace,
  WebGLRenderer,
} from 'three'
import { buildEnvironment, type BuiltEnvironment, type EnvPanel } from './environment'
import { drawingBufferFor, bufferChanged, type DrawingBuffer } from './renderSize'
import { ContextLossTracker, type ContextLossReport } from './contextLoss'
import { TIER_BUDGETS, type RenderTier } from './tiers'

/**
 * Frames drawn and thrown away before a calibration measures anything.
 *
 * Two, which is what 08 §6 asks for and also what the work needs: the first compiles and links
 * every program the machine uses, the second is the first one that runs entirely from warm
 * state. A single warm-up frame leaves the texture uploads in the measured one.
 */
export const CALIBRATION_WARMUP = 2

/** Frames actually timed. The fastest of them is the answer. */
export const CALIBRATION_FRAMES = 3

/**
 * Builds everything in the scene, and is called again after a context loss.
 *
 * It must be idempotent in effect: called twice, the scene must contain one machine, not two.
 * The host clears the scene before each call so a builder does not have to remember to.
 */
export interface SceneBuilder {
  readonly environmentPanels?: readonly EnvPanel[]
  build(scene: Scene, tier: RenderTier): void
  /** Release GPU resources. Called before a rebuild and on teardown. */
  dispose(): void
  /**
   * Pose the scene for a frame, given the interpolation alpha the driver reported.
   *
   * `alpha` is the fraction of a simulation step not yet consumed. It is a *position between
   * two known states*, never a licence to integrate: a builder that advanced anything by
   * `alpha` would be a second simulation, and a slow device would play a different game.
   */
  update(alpha: number): void
}

export interface SceneHostOptions {
  readonly tier?: RenderTier
  /** Overridable so a test can drive sizing without a window. */
  readonly devicePixelRatio?: () => number
  /** Overridable for the same reason. Returns the canvas's CSS size. */
  readonly measure?: () => { width: number; height: number }
}

export class SceneHost {
  readonly scene = new Scene()
  readonly camera: PerspectiveCamera
  readonly renderer: WebGLRenderer

  private readonly canvas: HTMLCanvasElement
  private readonly builder: SceneBuilder
  private readonly loss = new ContextLossTracker()
  private readonly measure: () => { width: number; height: number }
  private readonly dpr: () => number

  /**
   * The prefiltered room, built once from the renderer and shared by every physical material.
   *
   * Here rather than in the scene builder because it needs a `WebGLRenderer` to prefilter and
   * because it must survive a context loss the same way the renderer does — a rebuilt scene
   * with no environment is a machine that turns from steel into grey plastic after the browser
   * hiccups, which is the sort of thing that gets reported as "it looked fine yesterday".
   */
  private environment: BuiltEnvironment | undefined

  private tierName: RenderTier
  private buffer: DrawingBuffer = { width: 1, height: 1, pixelRatio: 1 }
  private disposed = false

  /** Frames drawn. Frames *not* drawn because the context was gone are not counted. */
  frames = 0

  constructor(canvas: HTMLCanvasElement, builder: SceneBuilder, options: SceneHostOptions = {}) {
    this.canvas = canvas
    this.builder = builder
    this.tierName = options.tier ?? 'high'
    this.measure =
      options.measure ?? (() => ({ width: canvas.clientWidth, height: canvas.clientHeight }))
    this.dpr = options.devicePixelRatio ?? (() => globalThis.devicePixelRatio ?? 1)

    this.renderer = new WebGLRenderer({
      canvas,
      antialias: true,
      // Opaque, so the hardware fast-clear path is taken (ARCH-010, per Meta's WebXR guidance).
      alpha: false,
      powerPreference: 'high-performance',
      failIfMajorPerformanceCaveat: false,
    })
    this.renderer.setClearColor(new Color(0x000000), 1)
    this.renderer.outputColorSpace = SRGBColorSpace
    this.renderer.toneMapping = ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.1

    // A pinball cabinet is about 1.3 m long and viewed from roughly a metre away, so the near
    // plane has to be close or the lockdown bar clips through the camera when a player leans in.
    this.camera = new PerspectiveCamera(50, 1, 0.02, 40)

    canvas.addEventListener('webglcontextlost', this.onContextLost, false)
    canvas.addEventListener('webglcontextrestored', this.onContextRestored, false)

    this.resize()
    this.applyEnvironment()
    this.builder.build(this.scene, this.tierName)
  }

  /**
   * Build the room and hand it to the scene.
   *
   * Rebuilt after a context loss along with everything else: the prefiltered map lives in a
   * render target, and a render target from a dead context is a black texture. Keeping the old
   * one would turn every chrome surface in the machine matte grey the moment the browser
   * hiccups — which reads as a lighting bug, not as a lost context.
   */
  private applyEnvironment(): void {
    this.environment?.dispose()
    this.environment = buildEnvironment(this.renderer, this.builder.environmentPanels)
    this.scene.environment = this.environment.texture
  }

  get tier(): RenderTier {
    return this.tierName
  }

  get contextLoss(): ContextLossReport {
    return this.loss.report()
  }

  /** True when the GPU is present and the scene is built. The simulation ignores this. */
  get renderable(): boolean {
    return !this.disposed && this.loss.renderable
  }

  /**
   * Change tier (M3-36 steps this down; settings can force it).
   *
   * Rebuilds, because a tier decides geometry and materials, not just resolution — and
   * re-sizes, because it also decides the pixel budget.
   */
  setTier(tier: RenderTier): void {
    if (tier === this.tierName) return
    this.tierName = tier
    this.rebuild()
    this.resize(true)
  }

  /**
   * Match the drawing buffer to the canvas.
   *
   * Cheap to call every frame and intended to be: a `ResizeObserver` misses a device-pixel
   * ratio change from dragging a window between monitors, and a `matchMedia` listener for the
   * ratio misses a CSS-driven layout change. Asking is more reliable than being told, and
   * `bufferChanged` makes asking nearly free.
   */
  resize(force = false): void {
    // **Not while a headset is presenting.** The XR session owns the framebuffer and its size,
    // and Three says so once per call — the machine runtime asks every frame, so entering VR
    // produced a warning at 72 Hz and a renderer being told two different sizes by two owners.
    // Guarded here rather than at the caller because the host owns the renderer, and every future
    // caller of `resize` would have the same problem for the same reason.
    if (this.renderer.xr.isPresenting) return
    const { width, height } = this.measure()
    const next = drawingBufferFor(width, height, this.dpr(), TIER_BUDGETS[this.tierName])
    if (!force && !bufferChanged(this.buffer, next)) return
    this.buffer = next
    this.camera.aspect = next.width / next.height
    this.camera.updateProjectionMatrix()
    if (this.renderable) {
      this.renderer.setPixelRatio(next.pixelRatio)
      // `false` — do not let Three write CSS width/height back onto the canvas. The page's
      // layout owns that, and a renderer that also sets it fights the stylesheet and wins,
      // which reads as the canvas growing a little every frame.
      this.renderer.setSize(width, height, false)
    }
  }

  /**
   * Rebuild the scene from the builder, as if the context had been lost.
   *
   * The harness rebuilds the machine on `R`, which replaces the `World` wholesale — physically
   * that is a new machine, and a scene still holding the old one's flippers would pose meshes
   * from a simulation nobody is running any more. Exactly as invalidating as losing the GPU,
   * so it takes exactly the same path.
   */
  rebuildScene(): void {
    if (!this.renderable) return
    this.rebuild()
  }

  /** The current drawing buffer, for the HUD and for tests. */
  get drawingBuffer(): DrawingBuffer {
    return this.buffer
  }

  /**
   * Draw a few frames and report what one costs, in milliseconds (M3-35, 08 §6 step 2).
   *
   * 08 §6: "Render two frames of a calibration scene and measure." The calibration scene is the
   * **machine itself**, at the tier the probe permitted, because a separate calibration scene
   * measures the speed of something this product never draws — and the first frames of the real
   * machine have to be paid for anyway.
   *
   * Two things about the measurement matter more than the arithmetic:
   *
   *   * **`finish()`, or the number is fiction.** `renderer.render()` queues commands and
   *     returns; the GPU may not have started. Without a synchronisation point this measures how
   *     fast the CPU can write a command buffer, which is roughly the same on a workstation and
   *     on a decade-old integrated chip — so the one device that most needs stepping down is the
   *     one that would measure fastest.
   *   * **The first frames are compilation, not rendering.** Every shader program in the machine
   *     is compiled and linked on its first use and every texture is uploaded once. Charging
   *     that to the frame rate would put a fast GPU on `low` for the rest of the session.
   *
   * The **fastest** of the measured frames is taken, not the average. Anything above the
   * minimum is contamination — a collection, another tab, the compositor — and the question
   * being asked is what this device can do, not what it happened to be doing.
   */
  calibrate(now: () => number = () => performance.now()): number {
    if (!this.renderable) return 0
    const gl = this.renderer.getContext() as { finish?: () => void } | null

    for (let i = 0; i < CALIBRATION_WARMUP; i++) {
      this.builder.update(0)
      this.renderer.render(this.scene, this.camera)
    }
    gl?.finish?.()

    let fastest = Number.POSITIVE_INFINITY
    for (let i = 0; i < CALIBRATION_FRAMES; i++) {
      const started = now()
      this.builder.update(0)
      this.renderer.render(this.scene, this.camera)
      gl?.finish?.()
      const took = now() - started
      if (took < fastest) fastest = took
    }
    // The calibration frames are frames: counting them keeps `frames` meaning "pictures this
    // host has drawn", which is what the soak test and the HUD read it as.
    this.frames += CALIBRATION_WARMUP + CALIBRATION_FRAMES
    return Number.isFinite(fastest) ? fastest : 0
  }

  /**
   * Draw one frame at the driver's interpolation alpha.
   *
   * Does nothing while the context is gone, and says so by returning false rather than by
   * throwing: the caller's loop keeps running, the simulation keeps stepping, and the picture
   * comes back when the GPU does.
   */
  render(alpha: number): boolean {
    if (!this.renderable) return false
    this.builder.update(alpha)
    this.renderer.render(this.scene, this.camera)
    this.frames++
    return true
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.canvas.removeEventListener('webglcontextlost', this.onContextLost, false)
    this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored, false)
    this.builder.dispose()
    this.scene.clear()
    this.environment?.dispose()
    this.environment = undefined
    this.renderer.dispose()

    /*
      **`dispose()` frees the resources; it does not free the context** (M11-02).

      A browser hands out roughly sixteen live WebGL contexts per page and then starts silently
      killing the oldest. `WebGLRenderer.dispose()` releases textures, programs and render lists and
      leaves the context itself alive, so every mount of the stage took one more of the sixteen and
      never gave it back. Measured: **100 navigations between the machine and the arcade produced 85
      "Too many active WebGL contexts. Oldest context will be lost." warnings** — one per mount from
      about the sixteenth onwards.

      Nothing visibly broke, because the contexts being killed belonged to renderers that had
      already been disposed. That is luck rather than design: the browser chooses the *oldest*, not
      the deadest, and on a Quest — where the limit is lower and a headset session holds a context of
      its own — the one it chooses would eventually be a live one, and a live context lost on Quest
      Browser is the one thing this renderer cannot recover from.

      `forceContextLoss` is Three's way of saying `WEBGL_lose_context.loseContext()`, which is the
      same call `capability.ts` already makes after its probe — for the same reason, written down in
      the same words, one file away.

      **After the listeners are removed**, deliberately: forcing a loss fires `webglcontextlost`, and
      a handler still attached would run the recovery path on a renderer that is being thrown away.
    */
    this.releaseContext()
  }

  /**
   * Give the WebGL context back — **unless the canvas is going to be used again**.
   *
   * `forceContextLoss` is the right thing when the canvas is being thrown away with the component,
   * which is what happens on every real navigation: React unmounts the subtree and the next mount
   * gets a fresh `<canvas>`. It is the *wrong* thing when the same canvas element is about to be
   * handed to a new renderer, because a canvas whose context has been lost hands the next
   * `getContext` a lost one, and the renderer built on it throws.
   *
   * That is not hypothetical — it is what React's **StrictMode** does in development: unmount and
   * remount the effect without touching the DOM, so the second `MachineView` meets the canvas the
   * first one just released. Measured as one failed view construction per mount.
   *
   * Deferred by a task so the answer is read *after* React has finished: cleanup runs before the
   * DOM is detached, so asking at dispose time would always say "still connected". A canvas that
   * has left the document is one nothing will reuse; a canvas still in it is one somebody wants.
   */
  private releaseContext(): void {
    const canvas = this.canvas
    const renderer = this.renderer
    setTimeout(() => {
      if (canvas.isConnected) return
      renderer.forceContextLoss()
    }, 0)
  }

  // --- context loss ----------------------------------------------------------------

  private readonly onContextLost = (event: Event): void => {
    // Without this the browser treats the loss as permanent and never fires `restored`. It is
    // one line, it has no visible effect when it works, and omitting it makes every recovery
    // path in this file unreachable.
    event.preventDefault()
    this.loss.onLost()
  }

  private readonly onContextRestored = (): void => {
    if (!this.loss.onRestored()) return
    try {
      this.rebuild()
      this.resize(true)
      this.loss.onRebuilt()
    } catch {
      // A rebuild can fail if the context is lost again while it runs, which is common on a
      // driver that is already in trouble. Back to `lost`, and the next restore tries again.
      this.loss.onRebuildFailed()
    }
  }

  private rebuild(): void {
    this.builder.dispose()
    this.scene.clear()
    this.applyEnvironment()
    this.builder.build(this.scene, this.tierName)
  }
}
